import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router'
import {
  IPTV_CHANNEL_PLAYLIST,
  GET_ACTIVE_SUBSCRIPTIONS,
  IPTV_CHANNEL_FILTER,
  IPTV_CHANNEL_CONTENT_URL,
} from '../../../utils/apiUrls'
import useAxios from 'axios-hooks'
import { addAdParams, create_share_url, getImage } from '../../../utils/helper'
import { AppContext } from '../../../context/app'
import { intersectionWith, isEmpty, isEqual } from 'lodash'
import { SubscriptionContext } from '../../../context/subscription'
import moment from 'moment'
import { useInterval } from '../../../hooks/useInterval'
import { useTranslation } from 'react-i18next'
import ChannelPlayer from '../../player/channelPlayer'
import IptvPlaylistEPG from '../iptvPlaylistEPG'
import CustomImage from '../../../components/customImage/customImage'
import LockIcon from '../../../assets/lock-icon.svg'
import { withTheme } from 'theming'
// import IptvFavorite from '../../../assets/imgComponents/iptvFavorite'
import IptvShare from '../../../assets/imgComponents/iptvShare'
import CategoryNavigatorCards from '../../../components/template/default/categoryNavigatorCards'
import ShareModal from '../../../components/shareModal/shareModal'

const IptvDefaultPage = ({ theme, data }) => {
  const channelId = useParams().channelId
  const { t } = useTranslation()
  const { isLogin, setOpenLoginModal, setModalType, accountObj } = useContext(AppContext)
  const {
    activeSubscription,
    setOpenSubscriptionModal,
    setSubscriptions,
    setRedirectionUrl,
    setContentPartners,
  } = useContext(SubscriptionContext)

  const [detailObj, setDetailObj] = useState({})
  const [isChannelSubscribed, setIsChannelSubscribed] = useState(false)
  const [currentPlayingContent, setCurrentPlayingContent] = useState({})
  const [videoSource, setVideoSource] = useState(null)
  const [videoURLError, setVideoURLError] = useState('')
  const [upNextPlaylist, setUpNextPlaylist] = useState([])
  const [iptvLoader, setIptvLoader] = useState(false)
  const [informaticButtonHover, setInformaticButtonHover] = useState('')
  const [shareModal, setShareModal] = useState(false)

  const [{ data: channelDetails, loading: channelDetailsLoading }, channelDetailsCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const [{ loading: activeSubLoading }, activeSubCall] = useAxios(
    {
      url: GET_ACTIVE_SUBSCRIPTIONS,
      method: 'get',
    },
    { manual: true },
  )

  const [{ loading: currentPlayingUrlLoading }, getCurrentPlayingUrl] = useAxios(
    {
      url: `${IPTV_CHANNEL_CONTENT_URL}/${channelId}`,
      method: 'get',
    },
    { manual: true },
  )

  const [, playlistPollingCall] = useAxios(
    {
      url: `${IPTV_CHANNEL_PLAYLIST}/${channelId}`,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    setIptvLoader(channelDetailsLoading || activeSubLoading)
  }, [channelDetailsLoading, activeSubLoading])

  useEffect(() => {
    setIsChannelSubscribed(true)

    setVideoSource(null)
    setVideoURLError('')
    setCurrentPlayingContent({})
    setUpNextPlaylist([])

    channelDetailsCall({
      data: {
        filters: { channel_ids: [channelId] },
        page: '1',
      },
    })
  }, [channelId])

  const normalizePlaylistData = (playlist = []) => {
    return (playlist || [])
      .filter((program) => moment.utc(program?.start_time).isValid())
      .map((program) => ({
        ...program,
        title: program?.title || program?.name || program?.file_name || 'Untitled Program',
        description: program?.description || '',
      }))
      .sort(
        (first, second) =>
          moment.utc(first.start_time).valueOf() - moment.utc(second.start_time).valueOf(),
      )
  }

  const checkPlanExist = (contentActiveSubscription, userSubscription) => {
    return !isEmpty(intersectionWith(contentActiveSubscription, userSubscription, isEqual))
  }

  const emptySubcheck = async (channelSub) => {
    const subRes = await activeSubCall()
    const activeSub = subRes.data.data
      .filter((item) => item.status)
      .map((item) => item.subscription)

    return !isEmpty(intersectionWith(channelSub, activeSub, isEqual))
  }

  const checkRestriction = async (channelDetail, openModal = false) => {
    if (!channelDetail?.is_restricted) {
      setIsChannelSubscribed(true)
      return true
    }

    if (!isLogin) {
      if (openModal) {
        setModalType('login')
        setOpenLoginModal(true)
      }
      setIsChannelSubscribed(false)
      return false
    }

    const channelSub = [
      ...(channelDetail?.active_subscription || []).map((item) => item._id),
      ...(channelDetail?.active_ppv || []).map((item) => item._id),
    ]

    const planExist = isEmpty(activeSubscription)
      ? await emptySubcheck(channelSub)
      : checkPlanExist(channelSub, activeSubscription)

    if (!planExist) {
      setIsChannelSubscribed(false)
      if (openModal) {
        setRedirectionUrl(window.location.pathname)
        setContentPartners(channelDetail?.owner ? channelDetail.owner : [])
        setSubscriptions(channelSub)
        setOpenSubscriptionModal(true)
      }
      return false
    }

    setIsChannelSubscribed(true)
    return true
  }

  const fetchCurrentPlayingContent = async () => {
    if (!isChannelSubscribed) return

    const requestSentAtMs = Date.now()

    try {
      const response = await getCurrentPlayingUrl()
      const result = response?.data

      if (!result?.success || isEmpty(result?.data)) return

      const { current_playing_url: playUrl, current_program: program } = result.data

      if (isEmpty(program)) {
        setCurrentPlayingContent({})
        setVideoSource(null)
        setVideoURLError('')
        return
      }

      // `offset_seconds` reflects the program's position as of the server's response, not
      // the moment we actually apply it here. Compensate for the round-trip network delay so
      // the resume position stays aligned with the true live position instead of lagging behind.
      const networkDelaySeconds = Math.max(0, (Date.now() - requestSentAtMs) / 1000)

      const nextContent = {
        ...program,
        title: program?.title || program?.name || program?.file_name || 'Untitled Program',
        playUrl,
        offset_seconds: Number(program?.offset_seconds || 0) + networkDelaySeconds,
      }

      // Always refresh program metadata (safe: does not remount the player).
      setCurrentPlayingContent(nextContent)

      if (!playUrl) {
        setVideoSource(null)
        setVideoURLError('Unable to play this program. Stream URL is missing.')
        return
      }

      // Only swap the active source when the playing asset actually changes,
      // so an unchanged program keeps playing smoothly across polls.
      if (playUrl === videoSource) return

      setVideoURLError('')

      // if (program?.is_hls_url) {
      //   const finalUrl = addAdParams(playUrl, {
      //     AV_CONTENT_URL: 'https://kalingo.tv/channel/6a848f274d2b92d00f196e65',
      //     AV_TIMESTAMP: moment().unix(),
      //     AV_CONTENT_TITLE: program?.name,
      //     AV_CONTENT_CAT: 'Travel',
      //     AV_CONTENT_KEYWORDS: 'Travel, Lifestyle, Hotel, Luxury',
      //     AV_CONTENT_NETWORK: 'Kalingo',
      //     // AV_CONTENT_ID: 'TRAVEL-TV-ENG',
      //     AV_CONTENT_CHANNEL: '4K Travel',
      //     AV_CONTENT_CONTEXT: 'video',
      //     AV_CONTENT_GENRE: 'Documentary',
      //     AV_CONTENT_LANGUAGE: 'en',
      //     AV_CONTENT_RATING: 'G',
      //   })
      //   setVideoSource(finalUrl)
      // } else {
      setVideoSource(playUrl)
      // }
    } catch (error) {
      console.error('Current playing content fetch failed', error)
    }
  }

  const fetchPlaylist = async () => {
    if (!isChannelSubscribed) return

    try {
      const startTimeUtc = moment().startOf('day').utc().toISOString()
      const endTimeUtc = moment().startOf('day').add(2, 'day').utc().toISOString()

      const response = await playlistPollingCall({
        params: {
          start_time: startTimeUtc,
          end_time: endTimeUtc,
        },
      })
      const result = response?.data

      if (!result?.success || isEmpty(result?.data)) return

      setUpNextPlaylist(normalizePlaylistData(result?.data?.playlist_data || []))
    } catch (error) {
      console.error('Playlist fetch failed', error)
    }
  }

  useEffect(() => {
    const syncChannelDetails = async () => {
      if (!channelDetails || !channelDetails.success || isEmpty(channelDetails?.data)) return

      const channelDetail = channelDetails.data[0]
      setDetailObj(channelDetail)

      const isAllowed = await checkRestriction(channelDetail)
      if (!isAllowed) {
        setUpNextPlaylist([])
        setCurrentPlayingContent({})
        setVideoSource(null)
        return
      }

      await fetchPlaylist()
      await fetchCurrentPlayingContent()
    }

    syncChannelDetails()
  }, [channelDetails, isLogin, activeSubscription])

  useInterval(() => {
    fetchPlaylist()
  }, 30000)

  useInterval(() => {
    fetchCurrentPlayingContent()
  }, 10000)

  const getStyleValue = (parentKey, childKey, defaultValue) => {
    if (!data?.style_config) {
      return defaultValue ? defaultValue : ''
    }

    let tempArr = data?.style_config[parentKey]?.data.filter((info) => info.key === childKey)

    return !isEmpty(tempArr) ? (tempArr[0].value ? tempArr[0].value : defaultValue) : defaultValue
  }

  const formatProgramStartTime = (program) => {
    if (!program?.start_time) return '--'

    const startMoment = moment.utc(program.start_time)
    if (!startMoment.isValid()) return '--'

    return startMoment.local().format('hh:mm A')
  }

  const getResumeTime = () => {
    if (isEmpty(currentPlayingContent)) return 0

    const offsetSeconds = Number(currentPlayingContent?.offset_seconds)
    if (!(offsetSeconds > 0)) return 0

    const maxDuration =
      Number(currentPlayingContent?.duration) > 0
        ? Math.floor(Number(currentPlayingContent.duration))
        : null

    const resumeSeconds = Math.floor(offsetSeconds)
    if (!maxDuration) return resumeSeconds
    return Math.min(resumeSeconds, Math.max(0, maxDuration - 1))
  }

  const getProgramTilesData = () => {
    const onAirProgram = !isEmpty(currentPlayingContent) ? currentPlayingContent : null
    const nowUtc = moment.utc()

    const upcomingPrograms = upNextPlaylist
      .filter((item) => moment.utc(item?.start_time).isAfter(nowUtc))
      .slice(0, 2)

    return {
      onAirProgram,
      upcomingPrograms,
    }
  }

  const { onAirProgram, upcomingPrograms } = getProgramTilesData()

  let componentProps = {
    componentData: {
      id: 'asdasda132132321dasdasda',
      data: { ...data.component_data },
    },
    modifyData: () => {},
    setModalState: () => {},
    isEdit: false,
    onSwap: () => {},
    onDelete: () => {},
    onCopy: () => {},
    onAdd: () => {},
  }

  return (
    <div className="w-full h-full ">
      <div
        className={`${
          iptvLoader && 'animate-pulse'
        } w-full px-2 md:px-[6px] xl:px-[3.66032%] flex flex-col md:flex-row md:justify-between`}
      >
        <div
          style={{ background: '#11171f' }}
          className="w-full md:w-[26%] xl:w-[25.27%] h-[279px] md:h-auto rounded-md px-2 md:px-3 xl:px-[12.5px]
          py-[20px] md:py-3  xl:py-[20.1px] mt-2 md:mt-0 order-2 md:order-1"
        >
          <div className="w-full flex justify-center items-center  rounded overflow-hidden">
            <CustomImage
              imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.thumbnail?.website?.s3_key)}
              alt="logo"
              imgClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
              loaderClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
              imgStyle={{ aspectRatio: '1.7777' }}
              loaderStyle={{ aspectRatio: '1.7777' }}
            />
          </div>
          <div
            style={{ color: getStyleValue('channel_profile', 'title_color', '#f8f8f8') }}
            className="text-center w-full line-clamp-2 text-xs md:text-lg 2xl:text-xl font-semibold mt-2.5"
          >
            {detailObj?.name}
          </div>
          <div
            style={{ flexWrap: 'wrap', rowGap: '8px' }}
            className=" w-full flex justify-center items-center mt-[11px] xl:mt-[20px] "
          >
            {!isEmpty(detailObj?.category) &&
              [...detailObj?.category]
                .filter((e, idx) => idx < 2)
                .map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        color: getStyleValue(
                          'channel_profile',
                          'category_video_text_color',
                          '#ffffff',
                        ),
                        background: getStyleValue(
                          'channel_profile',
                          'category_video_bg_color',
                          '#313945',
                        ),
                        padding: '0px 5.5px',
                        borderRadius: '3px',
                        marginLeft: index === 0 ? '0px' : '9.5px',
                      }}
                      className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
                    >
                      {item.name}
                    </div>
                  )
                })}

            <div
              style={{
                color: '#ffffff',
                background: '#0f5a22',
                padding: '0px 8.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              FREE-TO-AIR
            </div>

            <div
              style={{
                color: getStyleValue('channel_profile', 'category_video_text_color', '#ffffff'),
                background: getStyleValue('channel_profile', 'category_video_bg_color', '#313945'),
                padding: '0px 5.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              HD
            </div>
            <div
              style={{
                color: getStyleValue('channel_profile', 'category_video_text_color', '#ffffff'),
                background: getStyleValue('channel_profile', 'category_video_bg_color', '#313945'),
                padding: '0px 5.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              16:9
            </div>
          </div>
          <div
            style={{
              color: getStyleValue('channel_profile', 'description_color', '#acacac'),
              borderTop: '0.5px solid #28303b',
            }}
            className="w-full line-clamp-2 mt-3 pt-3 xl:pt-[1vw] px-3 xl:mt-[1.19326vw] text-[10px] md:text-[8px] lg:text-[11px] 2xl:text-sm"
          >
            {detailObj?.description}
          </div>
          <div className="w-full flex justify-center items-center mt-[28.5px] md:mt-5 px-3 md:px-[6px]">
            {/* <div
              style={{
                background: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'favorite' ? 'btn_bg_hover_color' : 'btn_bg_color',
                  informaticButtonHover === 'favorite' ? '363c45' : '#11171f',
                ),
                border: `0.5px solid ${getStyleValue(
                  'operational_icons_informative_icon',
                  'btn_border_color',
                  '#313945',
                )}`,
                color: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'favorite'
                    ? 'btn_icons_text_hover_color'
                    : 'btn_icons_text_color',
                  '#ffffff',
                ),
              }}
              className="w-[111px] md:w-[87.7px] lg:w-[121px] h-[27.5px] md:h-[18.8px] lg:h-[30px] flex justify-center items-center text-[10px] cursor-pointer rounded 2xl:text-sm 2xl:h-[35px] 2xl:w-[150px]"
              onMouseEnter={() => setInformaticButtonHover('favorite')}
              onMouseLeave={() => setInformaticButtonHover('')}
            >
              <div
                style={{
                  borderRight: `0.5px solid ${getStyleValue(
                    'operational_icons_informative_icon',
                    'btn_border_color',
                    '#313945',
                  )}`,
                }}
                className="w-[25px] md:w-[16.6px] lg:w-[27.5px] 2xl:w-[32.5px] h-full flex justify-center items-center"
              >
                <IptvFavorite
                  color={getStyleValue(
                    'operational_icons_informative_icon',
                    informaticButtonHover === 'favorite'
                      ? 'btn_icons_text_hover_color'
                      : 'btn_icons_text_color',
                    '#ffffff',
                  )}
                  className="w-[10.6px]  lg:w-[11.6px] 2xl:w-[15.4px] "
                />
              </div>
              <p
                style={{ whiteSpace: 'nowrap' }}
                className="mb-0 flex justify-center items-center w-[86px] md:w-[73.1px] lg:w-[93.5px] 2xl:w-[117.5px]"
              >
                Add to Favorite
              </p>
            </div> */}
            <div
              style={{
                background: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'share' ? 'btn_bg_hover_color' : 'btn_bg_color',
                  informaticButtonHover === 'share' ? '363c45' : '#11171f',
                ),
                border: `0.5px solid ${getStyleValue(
                  'operational_icons_informative_icon',
                  'btn_border_color',
                  '#313945',
                )}`,
                color: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'share'
                    ? 'btn_icons_text_hover_color'
                    : 'btn_icons_text_color',
                  '#ffffff',
                ),
              }}
              className={`w-[111px] md:w-[87.7px] lg:w-[121px] h-[27.5px] md:h-[18.8px] lg:h-[30px]  flex justify-center items-center text-[10px]  cursor-pointer rounded
               2xl:text-sm 2xl:h-[35px] 2xl:w-[150px]`}
              //  ml-[22.8px] md:ml-[8.6px] lg:ml-[25px]
              onMouseEnter={() => setInformaticButtonHover('share')}
              onMouseLeave={() => setInformaticButtonHover('')}
              onClick={() => {
                setShareModal(true)
              }}
            >
              <div
                style={{
                  borderRight: `0.5px solid ${getStyleValue(
                    'operational_icons_informative_icon',
                    'btn_border_color',
                    '#313945',
                  )}`,
                }}
                className="w-[25px] md:w-[16.6px] lg:w-[27.5px] 2xl:w-[32.5px] h-full flex justify-center items-center"
              >
                <IptvShare
                  color={getStyleValue(
                    'operational_icons_informative_icon',
                    informaticButtonHover === 'share'
                      ? 'btn_icons_text_hover_color'
                      : 'btn_icons_text_color',
                    '#ffffff',
                  )}
                  className="w-[10.2px] lg:w-[11.1px] 2xl:w-[15px] "
                />
              </div>
              <p
                style={{ whiteSpace: 'nowrap' }}
                className="mb-0 flex justify-center items-center w-[86px] md:w-[73.1px] lg:w-[93.5px] 2xl:w-[117.5px]"
              >
                Share Channel
              </p>
            </div>
          </div>
        </div>

        <div
          style={{ background: '#11171f', aspectRatio: '1.77777' }}
          className="md:w-[48%] xl:w-[46.6%] rounded-md overflow-hidden p-[5px] order-1 md:order-2"
        >
          {videoSource && (
            <ChannelPlayer
              contentInfo={currentPlayingContent}
              contentWatchUrl={videoSource}
              resumeTime={getResumeTime()}
              watermarkLogo={detailObj?.logo?.website?.s3_key}
              detailObj={detailObj}
              isIptvPlayer={true}
              pageData={data}
            />
          )}

          {videoURLError && !videoSource ? (
            <div className="w-full h-full flex justify-center items-center">
              <p
                style={{ color: '#cecece' }}
                className="text-xs md:text-base xl:text-lg text-center font-semibold"
              >
                {videoURLError}
              </p>
            </div>
          ) : null}

          {!videoSource ? (
            !isChannelSubscribed ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="flex px-5 md:px-0">
                  <img
                    src={LockIcon}
                    style={{ width: '18.5px', height: '24.6px' }}
                    alt="LockIcon"
                  />
                  <p
                    style={{ color: '#cecece' }}
                    className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                  >
                    {t('this_channel_not_included')}
                  </p>
                </div>
                <button
                  style={{ background: '#09e9e9', color: '#000' }}
                  className="w-[70%] text-xs md:text-base xl:text-lg rounded-md xl:rounded-lg mt-4 xl:mt-6 py-1"
                  onClick={() => checkRestriction(detailObj, true)}
                >
                  {t('upgrade_membership_to_watch')}
                </button>
              </div>
            ) : !currentPlayingUrlLoading && !channelDetailsLoading ? (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
                  imgAlt="channelimg"
                  imgClass="h-6 w-9 md:h-12 md:w-20"
                  loaderClass="h-6 w-9 md:h-12 md:w-20"
                />
                <p
                  style={{ color: '#cecece' }}
                  className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                >
                  {t('no_programs_shown_at_moment')}
                </p>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(
                    accountObj?.cf_domain_name,
                    detailObj?.thumbnail?.website?.s3_key,
                  )}
                  alt="logo"
                  imgClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
                  loaderClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
                  imgStyle={{ aspectRatio: '1.7777' }}
                  loaderStyle={{ aspectRatio: '1.7777' }}
                />
              </div>
            )
          ) : null}
        </div>

        <div
          className="h-[246px] md:h-auto w-full md:w-[22.083%] xl:w-[25.27%] flex flex-col justify-between items-center mt-2 md:mt-0 order-3
        "
        >
          <div
            style={{
              background: getStyleValue(
                'on_air_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[36.466%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[15px] md:pt-2 xl:pt-[1.244vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'on_air_program_information_section',
                    'on_air_text_color',
                    '#fe400f',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%] xl:w-[28.5%]"
              >
                On Air Now
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'on_air_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px] md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(onAirProgram)}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'on_air_program_information_section',
                  'on_air_program_title_color',
                  '#ffffff',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 font-bold text-[11px] md:text-[10px] line-clamp-3 lg:text-sm 2xl:text-base mt-[6px] xl:mt-[0.6222vw]"
            >
              {onAirProgram?.title || 'No program on air right now.'}
            </p>
          </div>
          <div
            style={{
              background: getStyleValue(
                'up_next_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[28.733%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[10.6px] md:pt-[6.8px] xl:pt-[0.8052vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'up_next_text_color',
                    '#e5e5e5',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%]  xl:w-[28.5%] "
              >
                Up Next
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px]  md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(upcomingPrograms[0])}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'up_next_program_information_section',
                  'up_next_program_title_color',
                  '#bebebe',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 text-[11px] line-clamp-2 xl:line-clamp-3 lg:text-xs 2xl:text-sm mt-[6px] xl:mt-[0.6222vw]"
            >
              {upcomingPrograms[0]?.title || 'No upcoming program scheduled.'}
            </p>
          </div>
          <div
            style={{
              background: getStyleValue(
                'up_next_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[28.733%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[10.6px] md:pt-[6.8px] xl:pt-[0.8052vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'up_next_text_color',
                    '#e5e5e5',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%]  xl:w-[28.5%] "
              >
                Up Next
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px]  md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(upcomingPrograms[1])}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'up_next_program_information_section',
                  'up_next_program_title_color',
                  '#bebebe',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 text-[11px] line-clamp-2 xl:line-clamp-3 lg:text-xs 2xl:text-sm mt-[6px] xl:mt-[0.6222vw]"
            >
              {upcomingPrograms[1]?.title || 'No upcoming program scheduled.'}
            </p>
          </div>
        </div>
      </div>
      {data.component_enabled ? (
        <div className="w-full">
          <CategoryNavigatorCards {...componentProps} iptvPage={true} />
        </div>
      ) : null}

      <div className="w-full  mx-auto mt-[21px] flex justify-center items-center px-2 md:px-[6px] xl:px-[3.66032%]">
        <IptvPlaylistEPG currentPlayingContent={currentPlayingContent} removeCategories={true} />
      </div>
      <div className="w-full  mx-auto mt-4 px-2 md:px-[6px] xl:px-[3.66032%]">
        <div className="flex items-center gap-2 xl:gap-5 text-[10px] xl:text-xs 2xl:text-sm text-white py-1">
          <div className="flex items-center">
            <div
              className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
              style={{ background: '#11171F', border: '1px solid #727272' }}
            ></div>
            <span className="2xl:ml-3 xl:ml-[10px] ml-1">Program</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
              style={{ background: '#141446', border: '1px solid #1F51FF' }}
            ></div>
            <span className="2xl:ml-3 xl:ml-[10px] ml-1">Streaming Now</span>
          </div>
        </div>
      </div>

      {shareModal && (
        <ShareModal
          onClose={() => setShareModal(false)}
          link={create_share_url()}
          detailObj={detailObj}
        />
      )}
    </div>
  )
}

export default withTheme(IptvDefaultPage)
